import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { ClockworkHud } from '../components/ClockworkHud';
import { FormattedMessage } from '../components/FormattedMessage';
import { ToolUsageAccordion } from '../components/ToolUsageAccordion';
import { ChatMessage, ChatAttachment } from '../types';
import { sendFissalChat, dispatchToImmichVault } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  getUsbDeviceStatus,
  requestUsbPermission,
  connectUsbSerial,
  UsbDeviceStatusResult
} from '../services/usbSerial';
import {
  checkShizuku,
  requestShizukuPermission,
  execShizuku,
  ShizukuStatus
} from '../services/shizuku';
import {
  ChatSession,
  getActiveSession,
  appendMessageToActiveSession,
  listSessions,
  createSession,
  deleteSession,
  setActiveSessionId,
  saveDraftInput,
  getDraftInput,
  clearAllSessions
} from '../services/chatStorage';
import {
  Send,
  Mic,
  MicOff,
  Sparkles,
  User,
  Bot,
  Copy,
  Check,
  RotateCcw,
  MessageSquare,
  Plus,
  Trash2,
  X,
  History,
  ShieldCheck,
  Zap,
  Paperclip,
  Image,
  FileText,
  Usb,
  Terminal
} from 'lucide-react';

export const FissalChatView: React.FC = () => {
  const { profile, isOnline, refreshStatus, edgeMode, edgeModel, setSettingsOpen, activeLayer } = useApp();

  // Iron Anchor: Persistent session & message binding
  const [currentSession, setCurrentSession] = useState<ChatSession>(() => getActiveSession(profile));
  const [input, setInput] = useState<string>(() => getDraftInput(profile));
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const [sessionDrawerOpen, setSessionDrawerOpen] = useState(false);
  const [sessionList, setSessionList] = useState<ChatSession[]>(() => listSessions(profile));

  // USB-C OTG & Shizuku AI Hardware Bridge State
  const [usbHookOpen, setUsbHookOpen] = useState<boolean>(activeLayer === 'devops');
  const [usbStatus, setUsbStatus] = useState<UsbDeviceStatusResult | null>(null);
  const [shizukuState, setShizukuState] = useState<ShizukuStatus | null>(null);
  const [hookingHardware, setHookingHardware] = useState<boolean>(false);

  // Ergonomics & Attachments State
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [immichDispatching, setImmichDispatching] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messages = currentSession.messages;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // Lifecycle Rehydration: Re-sync from storage whenever app is foregrounded or profile changes
  const rehydrate = useCallback(() => {
    const session = getActiveSession(profile);
    setCurrentSession(session);
    setSessionList(listSessions(profile));
    const draft = getDraftInput(profile);
    if (draft && !input) {
      setInput(draft);
    }
  }, [profile, input]);

  useEffect(() => {
    rehydrate();
  }, [profile, rehydrate]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        rehydrate();
      }
    };
    const handleFocus = () => rehydrate();
    const handleResume = () => rehydrate();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handleResume);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleResume);
    };
  }, [rehydrate]);

  useEffect(() => {
    const handleAssist = () => {
      triggerHaptic([40, 60, 40]);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };
    const handleHealthRationale = () => {
      setSettingsOpen(true);
    };

    window.addEventListener('fissal:assist_invoked', handleAssist);
    window.addEventListener('fissal:health_rationale_invoked', handleHealthRationale);
    return () => {
      window.removeEventListener('fissal:assist_invoked', handleAssist);
      window.removeEventListener('fissal:health_rationale_invoked', handleHealthRationale);
    };
  }, [setSettingsOpen]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 38), 150);
    textarea.style.height = `${nextHeight}px`;
  };

  const handleInputChange = (val: string) => {
    setInput(val);
    saveDraftInput(profile, val);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    if (e.key === 'Enter') {
      // Ctrl+Enter or Meta+Enter ALWAYS sends immediately on any platform
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        handleSend();
        return;
      }

      // On mobile / touch devices: Enter inserts a newline to prevent accidental sends from on-screen keyboard
      if (isTouchDevice) {
        setTimeout(adjustTextareaHeight, 10);
        return;
      }

      // On desktop physical keyboards: Shift+Enter inserts newline, plain Enter sends
      if (e.shiftKey) {
        setTimeout(adjustTextareaHeight, 10);
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>, isImage: boolean) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();

      reader.onload = () => {
        const base64Data = reader.result as string;
        const newAttachment: ChatAttachment = {
          id: `att_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          name: file.name,
          size: file.size,
          type: file.type || (isImage ? 'image/jpeg' : 'application/octet-stream'),
          data: base64Data,
          previewUrl: isImage ? base64Data : undefined
        };
        setAttachments(prev => [...prev, newAttachment]);
        triggerHaptic(25);
      };

      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
    triggerHaptic(15);
  };

  const handleDispatchToImmich = async (attachment: ChatAttachment) => {
    setImmichDispatching(attachment.id);
    triggerHaptic(30);
    try {
      await dispatchToImmichVault(attachment.data, attachment.name, attachment.type);
      setAttachments(prev => prev.map(a => a.id === attachment.id ? { ...a, dispatchedToImmich: true } : a));
      triggerHaptic([30, 50, 30]);
    } catch (err) {
      console.warn('[Immich] Dispatch error:', err);
    } finally {
      setImmichDispatching(null);
    }
  };

  const handleCopyMessage = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedMsgId(id);
    triggerHaptic(20);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  const handleCreateNewSession = () => {
    triggerHaptic([20, 30]);
    const newSession = createSession(profile);
    setCurrentSession(newSession);
    setSessionList(listSessions(profile));
    setSessionDrawerOpen(false);
  };

  const handleSwitchSession = (sessionId: string) => {
    triggerHaptic(20);
    setActiveSessionId(profile, sessionId);
    const session = getActiveSession(profile);
    setCurrentSession(session);
    setSessionDrawerOpen(false);
  };

  const handleDeleteSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    triggerHaptic([30, 40]);
    const nextSession = deleteSession(profile, sessionId);
    setCurrentSession(nextSession);
    setSessionList(listSessions(profile));
  };

  const handleClearAllSessions = () => {
    if (confirm('Clear all conversation archives for this profile?')) {
      triggerHaptic([40, 50, 40]);
      const fresh = clearAllSessions(profile);
      setCurrentSession(fresh);
      setSessionList(listSessions(profile));
      setSessionDrawerOpen(false);
    }
  };

  const handleSend = async (textToSend?: string) => {
    const query = (textToSend || input).trim();
    const currentAttachments = [...attachments];
    if ((!query && currentAttachments.length === 0) || loading) return;

    triggerHaptic(20);
    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      attachments: currentAttachments.length > 0 ? currentAttachments : undefined
    };

    // Immediate write-through to persistent session
    const sessionAfterUserMsg = appendMessageToActiveSession(profile, userMsg);
    setCurrentSession(sessionAfterUserMsg);
    setSessionList(listSessions(profile));

    if (!textToSend) {
      setInput('');
      saveDraftInput(profile, '');
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
    setLoading(true);

    try {
      const promptQuery = query || (currentAttachments.length > 0 ? 'Please inspect the attached image or file.' : '');
      const response = await sendFissalChat(
        promptQuery,
        sessionAfterUserMsg.messages,
        profile,
        undefined,
        currentAttachments
      );
      const assistantMsg: ChatMessage = {
        id: `fissal_${Date.now()}`,
        role: 'assistant',
        content: response.answer,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        tools: response.tools,
        provider: response.provider
      };

      // Immediate write-through of assistant response
      const sessionAfterAssistant = appendMessageToActiveSession(profile, assistantMsg);
      setCurrentSession(sessionAfterAssistant);
      setSessionList(listSessions(profile));
      triggerHaptic([20, 30, 20]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: `err_${Date.now()}`,
        role: 'assistant',
        content: `*Servos whir nervously.* \n\n**Communication relay error**: ${err.message}`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      const sessionAfterErr = appendMessageToActiveSession(profile, errorMsg);
      setCurrentSession(sessionAfterErr);
      setSessionList(listSessions(profile));
    } finally {
      setLoading(false);
    }
  };

  const toggleVoiceInput = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (isRecording) {
      setIsRecording(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsRecording(true);
        triggerHaptic(30);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          handleInputChange(transcript);
          handleSend(transcript);
        }
      };

      recognition.onerror = () => setIsRecording(false);
      recognition.onend = () => setIsRecording(false);
      recognition.start();
    } catch (e) {
      setIsRecording(false);
    }
  };

  // Refresh USB-C OTG & Shizuku status when hook bar is opened
  const refreshHardwareBridge = useCallback(async () => {
    try {
      const [usb, shiz] = await Promise.all([
        getUsbDeviceStatus().catch(() => null),
        checkShizuku().catch(() => null)
      ]);
      if (usb) setUsbStatus(usb);
      if (shiz) setShizukuState(shiz);
    } catch {}
  }, []);

  useEffect(() => {
    if (usbHookOpen) {
      refreshHardwareBridge();
    }
  }, [usbHookOpen, refreshHardwareBridge]);

  const handleHookUsbToFissal = async () => {
    if (hookingHardware) return;
    triggerHaptic([30, 50, 30]);
    setHookingHardware(true);
    try {
      await requestUsbPermission();
      await connectUsbSerial(115200);
      const shiz = await checkShizuku();
      if (shiz.running && !shiz.granted) {
        await requestShizukuPermission();
      }
      const [freshUsb, freshShiz] = await Promise.all([
        getUsbDeviceStatus(),
        checkShizuku()
      ]);
      setUsbStatus(freshUsb);
      setShizukuState(freshShiz);

      // Probe USB bus via Shizuku if granted
      let shizukuProbe = '';
      if (freshShiz.granted) {
        const probeRes = await execShizuku('dumpsys usb | head -n 25');
        if (probeRes.ok && probeRes.output) {
          shizukuProbe = ` Shizuku USB bus dump: ${probeRes.output.trim().slice(0, 350)}`;
        }
      }

      const devSummary = freshUsb.activeDevice
        ? `${freshUsb.activeDevice.productName || freshUsb.activeDevice.deviceName} (${freshUsb.activeDevice.vendorIdHex}:${freshUsb.activeDevice.productIdHex} @ ${freshUsb.baudRate} baud)`
        : `${freshUsb.deviceCount} OTG serial device(s) detected`;

      await handleSend(
        `I just hooked my phone's USB-C port & Shizuku bridge into you (${devSummary}; Shizuku granted=${freshShiz.granted}).${shizukuProbe} Inspect the connected device and tell me what commands or diagnostics we can run on it.`
      );
    } catch (e: any) {
      console.warn('Hardware hook error:', e);
    } finally {
      setHookingHardware(false);
    }
  };

  const isEdgeFallbackActive = edgeMode === 'sovereign_offline' || edgeMode === 'edge_first' || (!isOnline && edgeMode === 'auto');

  const echoChips = [
    '🔌 Inspect USB-C & Shizuku hardware',
    '🩺 Deep network & PM2 status check',
    '📡 Send AT+GMR over phone USB-C port',
    '🔄 Reload RedfurBot & Dashboard',
    '🧠 Search Qdrant memories'
  ];

  const edgeChipsAdmin = [
    'Probe Bastet sensors & EMF',
    'Read magnetometer µT',
    'Inspect local Gemma 4 vitals',
    'Homelab architecture topology',
    'Poll NFC transponder',
    'Explain sovereign air-gap'
  ];

  const familyChips = [
    '🩺 Run full network & camera check',
    '🛠️ Fix cameras & photo vault',
    '🐕 Check all cameras — are the dogs okay?',
    '🔌 Hook Fissal into USB-C device',
    '📝 Leave Echo a note'
  ];

  const edgeChipsFamily = [
    'Fissal offline status',
    'How do I backup offline notes?',
    'Is The Relay reachable?',
    'Explain offline mode'
  ];

  const isFamily = ['family', 'lycan', 'mom', 'onyx', 'trey', 'valen'].includes(profile) || activeLayer === 'family';

  const chips = isEdgeFallbackActive
    ? (activeLayer === 'family' ? edgeChipsFamily : edgeChipsAdmin)
    : (activeLayer === 'devops' ? echoChips : familyChips);

  const displayName =
    profile === 'echo'
      ? 'Echo 🗝️'
      : profile === 'lycan' || profile === 'mom'
      ? 'Lycan 🌸'
      : profile === 'onyx'
      ? 'Onyx 🐾'
      : profile === 'trey'
      ? 'Trey 🛡️'
      : profile === 'valen'
      ? 'Valen 👻'
      : 'Family';

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-transparent relative">
      {/* HUD Header - Shown in DevOps Suite */}
      {activeLayer === 'devops' && <ClockworkHud />}

      {/* Session Toolbar & USB-C Hardware Hook Toggle */}
      <div className="mx-3 mt-1 flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-xl bg-black/40 border border-white/10 text-xs font-mono">
        <button
          onClick={() => {
            setSessionList(listSessions(profile));
            setSessionDrawerOpen(true);
            triggerHaptic(20);
          }}
          className="flex items-center gap-1.5 text-slate-300 hover:text-white truncate max-w-[52%]"
          title="Open Conversation Archives"
        >
          <History className="w-3.5 h-3.5 text-fissal-gold shrink-0" />
          <span className="truncate text-[11px] font-medium text-amber-200/90">
            {currentSession.title}
          </span>
          <span className="text-[9px] text-slate-500 shrink-0">
            ({messages.length})
          </span>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => {
              triggerHaptic(15);
              setUsbHookOpen(!usbHookOpen);
            }}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-semibold transition-all active:scale-95 ${
              usbHookOpen || usbStatus?.connected
                ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                : 'bg-white/5 border-white/15 text-slate-300 hover:text-white'
            }`}
            title="Hook Fissal AI into Phone USB-C Port & Shizuku"
          >
            <Usb className="w-3 h-3" />
            <span>USB Hook</span>
          </button>

          <button
            onClick={handleCreateNewSession}
            className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-fissal-gold/15 hover:bg-fissal-gold/25 border border-fissal-gold/30 text-[10px] text-fissal-gold font-semibold transition-all active:scale-95"
            title="New Chat Session"
          >
            <Plus className="w-3 h-3" />
            <span>New</span>
          </button>
        </div>
      </div>

      {/* 🔌 USB-C OTG & SHIZUKU AI HARDWARE HOOK BAR */}
      {usbHookOpen && (
        <div className="mx-3 mt-1.5 p-2.5 rounded-xl bg-gradient-to-r from-emerald-950/70 via-slate-900/95 to-cyan-950/60 border border-emerald-500/35 flex flex-col gap-2 shadow-lg animate-in fade-in">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 shrink-0">
                <Usb className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-white flex items-center gap-1.5">
                  <span>All-in-One USB-C & Native AI Bridge</span>
                  <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono ${
                    usbStatus?.connected ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-slate-300'
                  }`}>
                    {usbStatus?.connected ? 'LINKED' : `${usbStatus?.deviceCount || 0} USB`}
                  </span>
                  <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-emerald-500/20 text-emerald-300">
                    {shizukuState?.shizukuElevated ? 'ADB UID 2000' : 'Built-in Engine Ready'}
                  </span>
                </div>
                <div className="text-[10px] text-slate-300 font-mono truncate">
                  {usbStatus?.activeDevice
                    ? `${usbStatus.activeDevice.productName || usbStatus.activeDevice.deviceName} (${usbStatus.activeDevice.vendorIdHex}:${usbStatus.activeDevice.productIdHex})`
                    : 'No extra apps needed — plug any USB-C device in or ask Fissal directly'}
                </div>
              </div>
            </div>

            <button
              onClick={handleHookUsbToFissal}
              disabled={hookingHardware || loading}
              className="px-2.5 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-bold text-[10px] font-mono flex items-center gap-1 shrink-0 shadow-md transition-all active:scale-95"
            >
              <Zap className="w-3 h-3" />
              <span>{hookingHardware ? 'Hooking...' : 'Hook AI to USB'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Sovereign Edge Fallback Indicator Banner */}
      {isEdgeFallbackActive && (
        <div className="mx-3 mt-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-950/80 via-slate-900 to-amber-950/60 border border-emerald-500/40 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            <span className="text-[11px] font-mono text-emerald-300 font-semibold flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-400" />
              {edgeMode === 'sovereign_offline'
                ? 'Air-Gapped Sovereign'
                : edgeMode === 'edge_first'
                ? 'Edge Silicon Priority'
                : !isOnline
                ? 'Offline Failover Active'
                : 'Edge Model Active'}
            </span>
            <span className="text-[10px] text-slate-400 font-mono hidden sm:inline">
              · {edgeModel.name} LiteRT
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                triggerHaptic(20);
                setSettingsOpen(true);
              }}
              className="px-2 py-0.5 rounded-lg text-[10px] font-mono bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 transition-all active:scale-95"
              title="Edge Settings"
            >
              Settings
            </button>
            <button
              onClick={() => {
                triggerHaptic(20);
                refreshStatus();
              }}
              className="px-2 py-0.5 rounded-lg text-[10px] font-mono bg-white/10 hover:bg-white/20 text-slate-200 border border-white/10 flex items-center gap-1 transition-all active:scale-95"
              title="Check server connection"
            >
              <RotateCcw className="w-2.5 h-2.5" /> Uplink
            </button>
          </div>
        </div>
      )}

      {/* Message Feed */}
      <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
          >
            {/* Message Metadata Header */}
            <div className="flex items-center gap-1.5 mb-1 px-1">
              {msg.role === 'user' ? (
                <>
                  <span className="text-[10px] text-slate-400 font-mono">{msg.timestamp}</span>
                  <span
                    className={`text-[11px] font-semibold flex items-center gap-1 ${
                      isFamily ? 'text-emerald-300' : 'text-fissal-gold'
                    }`}
                  >
                    {displayName} <User className="w-3 h-3" />
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[11px] font-semibold text-sky-300 flex items-center gap-1">
                    <Bot className="w-3.5 h-3.5 text-fissal-gold" /> Fissal
                  </span>
                  {msg.provider?.toLowerCase().includes('gemma') || msg.provider?.toLowerCase().includes('litert') ? (
                    <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-0.5">
                      <Zap className="w-2.5 h-2.5 text-amber-400" /> {msg.provider.toLowerCase().replace(' (litert edge)', '-litert')}
                    </span>
                  ) : (
                    <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-white/10 text-slate-300 border border-white/10">
                      {msg.provider ? msg.provider.replace('openwebui-', '').replace('openrouter-', '') : 'fissal-engine'}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-500 font-mono">{msg.timestamp}</span>
                </>
              )}
            </div>

            {/* Message Speech Bubble */}
            <div
              className={`max-w-[90%] rounded-2xl p-3.5 shadow-xl relative group transition-all ${
                msg.role === 'user'
                  ? isFamily
                    ? 'bg-gradient-to-br from-emerald-950/60 via-slate-900/90 to-black/80 border border-emerald-500/40 text-emerald-100 rounded-tr-sm'
                    : 'bg-gradient-to-br from-amber-950/40 via-slate-900/90 to-black/80 border border-fissal-gold/40 text-amber-100 rounded-tr-sm'
                  : msg.provider?.toLowerCase().includes('gemma') || msg.provider?.toLowerCase().includes('litert')
                  ? 'bg-gradient-to-br from-[#0c1f1a]/95 via-[#081512]/95 to-[#040b09] border border-emerald-500/35 text-slate-100 rounded-tl-sm shadow-emerald-500/10'
                  : 'bg-gradient-to-br from-[#0d1626]/90 via-[#0a101b]/95 to-[#070b13] border border-cyan-500/25 text-slate-100 rounded-tl-sm shadow-azure-glow/10'
              }`}
            >
              {/* Copy message button */}
              <button
                onClick={() => handleCopyMessage(msg.content, msg.id)}
                className="absolute top-2 right-2 p-1 rounded-lg bg-black/40 hover:bg-white/10 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                title="Copy text"
              >
                {copiedMsgId === msg.id ? (
                  <Check className="w-3 h-3 text-emerald-400" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>

              {/* Tool Execution Accordion */}
              {msg.role === 'assistant' && msg.tools && msg.tools.length > 0 && (
                <ToolUsageAccordion tools={msg.tools as any} isFamily={isFamily} />
              )}

              {/* Render User Attachments if present */}
              {msg.attachments && msg.attachments.length > 0 && (
                <div className="mb-2.5 flex flex-wrap gap-2">
                  {msg.attachments.map((att) => {
                    const isImg = att.type.startsWith('image/') || att.previewUrl;
                    return (
                      <div key={att.id} className="rounded-xl overflow-hidden border border-white/15 bg-black/40">
                        {isImg ? (
                          <img
                            src={att.previewUrl || att.data}
                            alt={att.name}
                            className="max-h-48 max-w-full object-contain rounded-lg"
                          />
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-2 text-xs font-mono text-slate-200">
                            <FileText className="w-4 h-4 text-fissal-gold shrink-0" />
                            <span className="truncate max-w-[180px]">{att.name}</span>
                            <span className="text-[10px] text-slate-400">({(att.size / 1024).toFixed(0)} KB)</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Formatted Message Body */}
              {msg.content && <FormattedMessage content={msg.content} isAssistant={msg.role === 'assistant'} />}
            </div>
          </div>
        ))}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-fissal-gold px-2 py-1 font-mono">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fissal-gold opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-fissal-gold"></span>
            </span>
            <span className="animate-pulse">
              {isEdgeFallbackActive ? 'Gemma 4 Edge calculating locally...' : 'Fissal gears turning...'}
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Action Chips */}
      <div className="px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0">
        {isEdgeFallbackActive && (
          <span className="shrink-0 px-2 py-0.5 rounded-md text-[10px] font-mono bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            EDGE
          </span>
        )}
        {chips.map((chip, idx) => (
          <button
            key={idx}
            onClick={() => handleSend(chip)}
            className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-all flex items-center gap-1 active:scale-95 ${
              isEdgeFallbackActive
                ? 'bg-emerald-950/40 hover:bg-emerald-900/50 border-emerald-500/30 text-emerald-200 hover:text-white'
                : 'bg-white/5 hover:bg-white/10 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <Sparkles className={`w-2.5 h-2.5 ${isEdgeFallbackActive ? 'text-emerald-400' : 'text-fissal-gold'}`} />
            {chip}
          </button>
        ))}
      </div>

      {/* Input Bar with Draft Persistence, Multiline Dynamic Folding, and Attachments */}
      <div className="p-2.5 bg-[#070a0f]/95 border-t border-white/10 shrink-0 relative">
        {/* Hidden File Inputs */}
        <input
          type="file"
          ref={imageInputRef}
          accept="image/*"
          className="hidden"
          onChange={(e) => handlePickFile(e, true)}
        />
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={(e) => handlePickFile(e, false)}
        />

        {/* Attachment Staging Tray */}
        {attachments.length > 0 && (
          <div className="mb-2 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            {attachments.map((att) => {
              const isImg = att.type.startsWith('image/') || att.previewUrl;
              return (
                <div
                  key={att.id}
                  className="relative group flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-slate-900/90 border border-fissal-gold/40 shadow-lg text-xs"
                >
                  {isImg ? (
                    <img
                      src={att.previewUrl || att.data}
                      alt={att.name}
                      className="w-10 h-10 object-cover rounded-lg border border-white/10 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-fissal-gold" />
                    </div>
                  )}

                  <div className="flex flex-col min-w-0 max-w-[120px] pr-1">
                    <span className="truncate text-[11px] font-medium text-slate-200">{att.name}</span>
                    <span className="text-[9px] text-slate-400 font-mono">{(att.size / 1024).toFixed(0)} KB</span>
                    {isImg && (
                      <button
                        type="button"
                        onClick={() => handleDispatchToImmich(att)}
                        disabled={att.dispatchedToImmich || immichDispatching === att.id}
                        className="mt-0.5 text-[9px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-60 disabled:cursor-default flex items-center gap-0.5"
                      >
                        {immichDispatching === att.id ? (
                          'Vaulting...'
                        ) : att.dispatchedToImmich ? (
                          <>
                            <Check className="w-2.5 h-2.5 text-emerald-400" /> Vaulted
                          </>
                        ) : (
                          '+ Immich'
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(att.id)}
                    className="p-1 rounded-full bg-black/60 hover:bg-rose-500/30 text-slate-400 hover:text-rose-300 transition-colors shrink-0"
                    title="Remove attachment"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Attachment Selection Menu Popover */}
        {attachmentMenuOpen && (
          <div className="absolute bottom-16 left-3 z-30 flex flex-col gap-1 p-1.5 rounded-2xl bg-[#0e1626] border border-white/20 shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-150">
            <button
              type="button"
              onClick={() => {
                setAttachmentMenuOpen(false);
                imageInputRef.current?.click();
              }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-200 hover:bg-white/10 hover:text-white transition-all text-left active:scale-95"
            >
              <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30">
                <Image className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Photo / Camera Roll</span>
                <span className="text-[10px] text-slate-400">Gemini Vision & Immich</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setAttachmentMenuOpen(false);
                fileInputRef.current?.click();
              }}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-200 hover:bg-white/10 hover:text-white transition-all text-left active:scale-95"
            >
              <div className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Document / File</span>
                <span className="text-[10px] text-slate-400">Log, JSON, or text</span>
              </div>
            </button>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex items-end gap-1.5"
        >
          {/* Attachment Paperclip Button */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic(15);
              setAttachmentMenuOpen((prev) => !prev);
            }}
            className={`p-2.5 rounded-xl border transition-all shrink-0 ${
              attachmentMenuOpen || attachments.length > 0
                ? 'bg-amber-500/20 border-fissal-gold text-fissal-gold'
                : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:bg-white/10'
            }`}
            title="Attach Image or Document"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {/* Multiline Dynamic Folding Textarea */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              enterKeyHint="enter"
              placeholder={
                isEdgeFallbackActive
                  ? '[Offline Sovereign] Command or query local Gemma 4...'
                  : isFamily
                  ? 'Ask Fissal about photos, cameras, or notes...'
                  : 'Command or query Fissal (Shift+Enter for newline)...'
              }
              className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-fissal-gold focus:ring-1 focus:ring-fissal-gold/50 transition-all resize-none max-h-36 overflow-y-auto leading-relaxed block"
              style={{ minHeight: '38px' }}
            />
          </div>

          {/* Voice Input Button */}
          <button
            type="button"
            onClick={toggleVoiceInput}
            className={`p-2.5 rounded-xl border transition-all shrink-0 ${
              isRecording
                ? 'bg-rose-500/20 border-rose-500 text-rose-400 animate-pulse'
                : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
            }`}
            title="Voice Input"
          >
            {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>

          {/* Submit / Send Button */}
          <button
            type="submit"
            disabled={(!input.trim() && attachments.length === 0) || loading}
            className="p-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold disabled:opacity-40 disabled:cursor-not-allowed shadow-quantum-glow transition-all active:scale-95 shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Session Drawer Modal */}
      {sessionDrawerOpen && (
        <div className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex flex-col justify-end">
          <div className="bg-[#0b101a] border-t border-white/15 rounded-t-3xl max-h-[80%] flex flex-col p-4 shadow-2xl animate-in slide-in-from-bottom duration-200">
            {/* Drawer Header */}
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-fissal-gold" />
                <div>
                  <h3 className="font-cinzel text-sm font-bold text-white tracking-wide">
                    Iron Anchor Archives
                  </h3>
                  <p className="text-[10px] text-slate-400 font-mono">
                    {sessionList.length} thread{sessionList.length !== 1 ? 's' : ''} stored locally
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleCreateNewSession}
                  className="px-3 py-1.5 rounded-xl bg-fissal-gold/20 border border-fissal-gold/40 text-fissal-gold hover:bg-fissal-gold/30 text-xs font-semibold flex items-center gap-1 transition-all active:scale-95"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Thread</span>
                </button>
                <button
                  onClick={() => setSessionDrawerOpen(false)}
                  className="p-1.5 rounded-xl text-slate-400 hover:text-white bg-white/5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Session List */}
            <div className="flex-1 overflow-y-auto py-3 space-y-2">
              {sessionList.map((s) => {
                const isActive = s.id === currentSession.id;
                const lastMsg = s.messages[s.messages.length - 1];
                const snippet = lastMsg ? lastMsg.content.substring(0, 70).replace(/\n/g, ' ') : 'Empty session';
                const formattedDate = new Date(s.updatedAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={s.id}
                    onClick={() => handleSwitchSession(s.id)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between group ${
                      isActive
                        ? 'bg-amber-500/15 border-fissal-gold/60 text-white shadow-quantum-glow'
                        : 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-300'
                    }`}
                  >
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs truncate">
                          {s.title}
                        </span>
                        {isActive && (
                          <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-fissal-gold text-slate-950 font-bold">
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">
                        {snippet}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-500 font-mono">
                        <span>{formattedDate}</span>
                        <span>•</span>
                        <span>{s.messages.length} messages</span>
                      </div>
                    </div>

                    <button
                      onClick={(e) => handleDeleteSession(e, s.id)}
                      className="p-2 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 opacity-60 group-hover:opacity-100 transition-all shrink-0"
                      title="Delete Thread"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Drawer Footer */}
            <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
              <span className="text-slate-500 font-mono">Iron Anchor v1.2.4</span>
              <button
                onClick={handleClearAllSessions}
                className="text-rose-400/80 hover:text-rose-300 text-[10px] font-mono hover:underline"
              >
                Clear all archives
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
